import React, { useRef, useEffect } from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Autoplay, Pagination, Navigation, EffectFade } from 'swiper/modules';
import { motion } from 'framer-motion';
import ThreeBackground from './ThreeBackground';

import 'swiper/css';
import 'swiper/css/pagination';
import 'swiper/css/navigation';
import 'swiper/css/effect-fade';

import slide1 from '../assets/sliders/slide1.jpeg';
import slide2 from '../assets/sliders/slide2.jpeg';
import slide3 from '../assets/sliders/slide3.jpeg';

const slides = [
  {
    image: slide1,
    label: 'New Arrival',
    title: 'Unveiling the Magic',
    sub: 'Experience a new dimension of storytelling.',
    year: '2026',
  },
  {
    image: slide2,
    label: 'Behind the Scenes',
    title: 'Creating the Impossible',
    sub: 'A glimpse into the art of modern cinema.',
    year: '2026',
  },
  {
    image: slide3,
    label: 'Grand Release',
    title: 'The Next Chapter',
    sub: 'Embark on an unforgettable journey.',
    year: '2026',
  },
  // {
  //   image: '/images/hero1.jpg',
  //   label: 'Epic Fantasy',
  //   title: 'Rise of the Golden Throne',
  //   sub: 'A saga of power, courage and destiny written in the stars',
  //   year: '2024',
  // },
  // {
  //   image: '/images/hero2.jpg',
  //   label: 'Behind the Lens',
  //   title: 'We Craft Worlds That Move You',
  //   sub: 'Every frame a masterpiece. Every story told with passion.',
  //   year: '',
  // },
  // {
  //   image: '/images/hero3.jpg',
  //   label: 'Royal Drama',
  //   title: 'The Palace of Eternal Light',
  //   sub: 'An opulent tale of royalty, intrigue and timeless love',
  //   year: '2025',
  // },
  // {
  //   image: '/images/hero4.jpg',
  //   label: 'Epic War',
  //   title: 'Flames of the Last Kingdom',
  //   sub: 'Thousands of warriors. One impossible stand. One legend born.',
  //   year: '2024',
  // },
  // {
  //   image: '/images/hero5.jpg',
  //   label: 'Grand Premiere',
  //   title: 'Where Stories Become Legends',
  //   sub: 'Magic Moon Studios — Cinematic excellence on a global stage',
  //   year: '',
  // },
];

const textVariants = {
  hidden: { opacity: 0, y: 60 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.18, duration: 0.8, ease: 'easeOut' },
  }),
};

export default function HeroSlider() {
  return (
    <section id="home" className="relative w-full h-screen min-h-[600px] overflow-hidden">
      {/* Three.js star layer */}
      <ThreeBackground type="stars" />

      <Swiper
        modules={[Autoplay, Pagination, Navigation, EffectFade]}
        effect="fade"
        speed={1200}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        pagination={{ clickable: true }}
        navigation
        loop
        className="w-full h-full"
        style={{ position: 'relative', zIndex: 2 }}
      >
        {slides.map((slide, idx) => (
          <SwiperSlide key={idx}>
            {({ isActive }) => (
              <div className="relative w-full h-full">
                {/* BG image */}
                <img
                  src={slide.image}
                  alt={slide.title}
                  className="absolute inset-0 w-full h-full object-cover"
                  style={{ filter: 'brightness(0.45)' }}
                />

                {/* Gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-r from-brand-black/80 via-transparent to-brand-black/40" />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-black/90 via-transparent to-transparent" />

                {/* Gold corner accent */}
                <div className="absolute top-0 left-0 w-32 h-32 border-t-2 border-l-2 border-brand-gold/40" />
                <div className="absolute bottom-0 right-0 w-32 h-32 border-b-2 border-r-2 border-brand-gold/40" />

                {/* Content */}
                <div className="absolute inset-0 flex items-center z-10">
                  <div className="max-w-7xl mx-auto px-6 lg:px-12 w-full">
                    <div className="max-w-2xl">
                      {isActive && (
                        <>
                          <motion.div
                            custom={0}
                            variants={textVariants}
                            initial="hidden"
                            animate="visible"
                            className="flex items-center gap-3 mb-4"
                          >
                            <div className="h-px w-10 bg-brand-orange" />
                            <span className="section-label">{slide.label}</span>
                            {slide.year && (
                              <span className="text-xs font-cinzel text-brand-gold/60 ml-2">
                                {slide.year}
                              </span>
                            )}
                          </motion.div>

                          <motion.h1
                            custom={1}
                            variants={textVariants}
                            initial="hidden"
                            animate="visible"
                            className="font-cinzel font-black text-4xl md:text-6xl lg:text-7xl leading-tight mb-6 text-gold-gradient"
                            style={{ textShadow: '0 0 60px rgba(243,112,33,0.3)' }}
                          >
                            {slide.title}
                          </motion.h1>

                          <motion.p
                            custom={2}
                            variants={textVariants}
                            initial="hidden"
                            animate="visible"
                            className="font-inter text-base md:text-lg text-white/70 mb-10 leading-relaxed max-w-lg"
                          >
                            {slide.sub}
                          </motion.p>

                          <motion.div
                            custom={3}
                            variants={textVariants}
                            initial="hidden"
                            animate="visible"
                            className="flex gap-4 flex-wrap"
                          >
                            <button
                              onClick={() =>
                                document
                                  .querySelector('#productions')
                                  ?.scrollIntoView({ behavior: 'smooth' })
                              }
                              className="btn-royal px-8 py-4 text-sm rounded-sm"
                            >
                              Explore Productions
                            </button>
                            <button
                              onClick={() =>
                                document
                                  .querySelector('#videos')
                                  ?.scrollIntoView({ behavior: 'smooth' })
                              }
                              className="border border-brand-gold/50 text-brand-gold font-cinzel text-sm tracking-widest uppercase px-8 py-4 rounded-sm hover:bg-brand-gold/10 transition-all duration-300"
                            >
                              Watch Showreel
                            </button>
                          </motion.div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </SwiperSlide>
        ))}
      </Swiper>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-2">
        <span className="font-cinzel text-[10px] tracking-widest text-white/40 uppercase">
          Scroll
        </span>
        <div className="w-px h-10 bg-gradient-to-b from-brand-orange to-transparent animate-pulse" />
      </div>
    </section>
  );
}
